import { Check, Link as LinkIcon, Linkedin, Mail, Share2, X as XIcon } from "lucide-react";
import { Suspense, lazy, useEffect, useState } from "react";

// Instagram Story generator draws on a canvas — lazy-load so it never
// competes with the page's own first paint.
const InstagramShareButton = lazy(() =>
  import("@/components/InstagramShare").then((m) => ({ default: m.InstagramShareButton })),
);

interface ShareRowProps {
  /** Absolute canonical URL of the page being shared. */
  url: string;
  title: string;
  /** Cover artwork used as the Story hero. */
  img?: string;
  /** Short hook / excerpt line shown on the Story. */
  excerpt?: string;
  /** Eyebrow above the Story headline. */
  category?: string;
  lang?: "tr" | "en";
  /** Group label; Turkish articles use "Yazıyı paylaş", the homepage "Sayfayı paylaş". */
  label?: string;
  className?: string;
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3Z" />
    </svg>
  );
}

/**
 * The site's one share row: native share (phones), copy link, LinkedIn,
 * Instagram, WhatsApp, e-mail, X. Every page that offers sharing uses this,
 * so the options and their order never drift between pages.
 */
export function ShareRow({
  url,
  title,
  img,
  excerpt,
  category,
  lang = "tr",
  label = "Yazıyı paylaş",
  className = "",
}: ShareRowProps) {
  const [copied, setCopied] = useState(false);
  const [canNative, setCanNative] = useState(false);
  useEffect(() => {
    setCanNative(typeof navigator !== "undefined" && typeof navigator.share === "function" && window.matchMedia("(pointer: coarse)").matches);
  }, []);
  const cls = "grid size-9 place-items-center rounded-full border border-ink/15 text-ink hover:border-ink disabled:opacity-50";
  const u = encodeURIComponent(url);
  return (
    <span className={`flex flex-wrap items-center gap-2 ${className}`} aria-label={label} role="group">
      {canNative && (
        <button
          type="button"
          className={cls}
          aria-label="Paylaş"
          onClick={() => { navigator.share({ title, url }).catch(() => {}); }}
        >
          <Share2 aria-hidden className="size-4" />
        </button>
      )}
      <button
        type="button"
        className={cls}
        aria-label={copied ? "Bağlantı kopyalandı" : "Bağlantıyı kopyala"}
        title={copied ? "Kopyalandı" : "Bağlantıyı kopyala"}
        onClick={() => {
          navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }, () => {});
        }}
      >
        {copied ? <Check aria-hidden className="size-4" /> : <LinkIcon aria-hidden className="size-4" />}
      </button>
      <a className={cls} aria-label="LinkedIn'de paylaş" target="_blank" rel="noopener noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`}>
        <Linkedin aria-hidden className="size-4" />
      </a>
      <Suspense fallback={null}>
        <InstagramShareButton
          url={url}
          title={title}
          img={img}
          excerpt={excerpt}
          category={category}
          lang={lang}
          iconSize={16}
          className={cls}
        />
      </Suspense>
      <a className={cls} aria-label="WhatsApp'ta paylaş" target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`}>
        <WhatsAppIcon className="size-4" />
      </a>
      <a className={cls} aria-label="E-postayla paylaş" href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n\n${url}`)}`}>
        <Mail aria-hidden className="size-4" />
      </a>
      <a className={cls} aria-label="X'te paylaş" target="_blank" rel="noopener noreferrer" href={`https://x.com/intent/tweet?url=${u}&text=${encodeURIComponent(title)}`}>
        <XIcon aria-hidden className="size-4" />
      </a>
    </span>
  );
}
